import React, { useEffect, useRef, useState } from 'react';
import Input from '../../../../../components/ui/inputs/Input';
import Select from '../../../../../components/ui/selects/Select';
import PhoneInput from '../../../../../components/ui/inputs/PhoneInput';
import Button from '../../../../../components/ui/buttons/Button';
import { fetchWithCsrf } from '../../../../../utils/fetchWithCsrf';
import { camelizeKeys } from '../../../../../utils/camelizeKeys';
import { snakecaseKeys } from '../../../../../utils/snakecaseKeys';
import Textarea from '../../../../../components/ui/inputs/Textarea';
import CpfInput from '../../../../../components/ui/inputs/CpfInput';
import DateInput from '../../../../../components/ui/inputs/DateInput';
import CepInput from '../../../../../components/ui/inputs/CepInput';
import BeneficiariesFormList from '../../../../../components/forms/profilePersonal/BeneficiaryFormList';
import FinancialOfficerFormList from '../../../../../components/forms/profileFinancialOfficer/FinancialOfficerFormList';
import { useToast } from '../../../../../components/ui/toast/ToastContext';

const PersonalTab = ({ clientId, onSuccess, canWrite }) => {
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    cpfCnpj: '',
    gender: '',
    maritalStatus: '',
    birthDate: '',
    nationality: '',
    birthPlace: '',
    rg: '',
    expeditionPlace: '',
    expeditionDate: '',
    address: '',
    zipcode: '',
    neighbourhood: '',
    state: '',
    city: '',
    complement: '',
    number: '',
    brokersRelationship: '',
    acceptNewInstitutions: '',
    workplace: '',
    indicatedBy: '',
    expectedResult: '',
    beneficiariesAttributes: [],
    financialOfficer: null,
    professionId: '',
  });

  const [genderOptions, setGenderOptions] = useState([]);
  const [professionOptions, setProfessionOptions] = useState([]);
  const [maritalStatusOptions, setMaritalStatusOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const { addToast } = useToast();
  const cepRunCount = useRef(0);

  const fetchClientData = () => {
    fetch(`/app/planner/client/${clientId}/profile/account`)
      .then((res) => res.json())
      .then((data) => {
        const client = camelizeKeys(data);

        setForm({
          name: client.name || '',
          email: client.email || '',
          phone: client.phone || '',
          cpfCnpj: client.cpfCnpj || '',
          gender: client.gender || '',
          maritalStatus: client.maritalStatus || '',
          birthDate: client.birthDate || '',
          nationality: client.nationality || '',
          birthPlace: client.birthPlace || '',
          rg: client.rg || '',
          expeditionPlace: client.expeditionPlace || '',
          expeditionDate: client.expeditionDate || '',
          address: client.address || '',
          zipcode: client.zipcode || '',
          neighbourhood: client.neighbourhood || '',
          state: client.state || '',
          city: client.city || '',
          complement: client.complement || '',
          number: client.number || '',
          brokersRelationship: client.brokersRelationship || '',
          acceptNewInstitutions: client.acceptNewInstitutions || '',
          workplace: client.workplace || '',
          indicatedBy: client.indicatedBy || '',
          expectedResult: client.expectedResult || '',
          beneficiariesAttributes: client.beneficiaries || [],
          financialOfficer: client.financialOfficer || null,
          professionId: client.professionId || '',
        });
      });
  };

  useEffect(() => {
    fetchClientData();
  }, [clientId]);

  useEffect(() => {
    fetch('/app/shared/form_options')
      .then((res) => res.json())
      .then((data) => {
        const camelData = camelizeKeys(data);
        setGenderOptions(camelData.genders?.map((opt) => ({ label: opt.label, value: opt.value })) || []);
        setMaritalStatusOptions(
          camelData.maritalStatuses?.map((opt) => ({ label: opt.label, value: opt.value })) || []
        );
        setProfessionOptions(camelData.professions.map((p) => ({ label: p.label, value: p.value })));
      });
  }, []);

  useEffect(() => {
    const cep = form.zipcode?.replace(/\D/g, '');

    cepRunCount.current += 1;

    // Pula as duas primeiras execuções
    if (cepRunCount.current <= 2) return;

    if (cep?.length === 8) {
      fetch(`https://viacep.com.br/ws/${cep}/json/`)
        .then((res) => res.json())
        .then((data) => {
          if (!data.erro) {
            setForm((prev) => ({
              ...prev,
              address: data.logradouro || '',
              neighbourhood: data.bairro || '',
              city: data.localidade || '',
              state: data.uf || '',
            }));
          }
        })
        .catch((err) => console.error('[CEP EFFECT] Erro ao buscar endereço por CEP:', err));
    }
  }, [form.zipcode]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    setErrorMessage('');
    const normalizedForm = {
      ...form,
      financial_officer_attributes: form.financialOfficer || null,
    };
    delete normalizedForm.financialOfficer;

    const payload = snakecaseKeys({ client: normalizedForm });

    try {
      const res = await fetchWithCsrf(`/app/planner/client/${clientId}/profile/account`, {
        method: 'PUT',
        body: JSON.stringify(payload),
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      });

      if (!res.ok) {
        const json = await res.json();
        throw new Error(json.message || 'Erro ao atualizar dados pessoais.');
      }

      addToast({ type: 'success', message: 'Dados pessoais salvos com sucesso!' });
      fetchClientData();
      onSuccess();
    } catch (err) {
      setErrorMessage(err.message);
      addToast({ type: 'error', message: err.message || 'Erro ao salvar dados pessoais.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-custom20 p-10 shadow">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit();
        }}
      >
        <h2 className="text-blueDarker text-xl font-semibold mb-4">Dados pessoais</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 font-jost text-sm">
          <Input required name="name" label="Nome completo" value={form.name} onChange={handleChange} disabled={!canWrite} />
          <Input required name="email" label="E-mail" value={form.email} onChange={handleChange} disabled={!canWrite} />
          <PhoneInput required name="phone" label="Telefone" value={form.phone} onChange={handleChange} disabled={!canWrite} />
          <CpfInput required name="cpfCnpj" label="CPF" value={form.cpfCnpj} onChange={handleChange} disabled={!canWrite} />
          <Select
            required
            name="gender"
            label="Sexo"
            value={form.gender}
            onChange={handleChange}
            options={genderOptions}
            disabled={!canWrite}
          />
          <Select
            required
            name="maritalStatus"
            label="Estado civil"
            value={form.maritalStatus}
            onChange={handleChange}
            options={maritalStatusOptions}
            disabled={!canWrite}
          />
          <DateInput
            required
            name="birthDate"
            label="Data de nascimento"
            value={form.birthDate}
            onChange={handleChange}
            disabled={!canWrite}
          />
          <Input required name="nationality" label="Nacionalidade" value={form.nationality} onChange={handleChange} disabled={!canWrite} />
          <Input required name="birthPlace" label="Naturalidade" value={form.birthPlace} onChange={handleChange} disabled={!canWrite} />

          <CepInput required name="zipcode" label="CEP" value={form.zipcode} onChange={handleChange} disabled={!canWrite} />
          <Input required name="state" label="Estado" value={form.state} onChange={handleChange} disabled={!canWrite} />
          <Input required name="city" label="Cidade" value={form.city} onChange={handleChange} disabled={!canWrite} />
          <Input required name="neighbourhood" label="Bairro" value={form.neighbourhood} onChange={handleChange} disabled={!canWrite} />
          <Input required name="address" label="Endereço" value={form.address} onChange={handleChange} disabled={!canWrite} />
          <Input required name="number" label="Número" value={form.number} onChange={handleChange} disabled={!canWrite} />
          <Input required name="complement" label="Complemento" value={form.complement} onChange={handleChange} disabled={!canWrite} />

          <Input required name="workplace" label="Local de trabalho" value={form.workplace} onChange={handleChange} disabled={!canWrite} />
          <Select
            required
            name="professionId"
            label="Profissão"
            value={form.professionId}
            onChange={handleChange}
            options={professionOptions}
            disabled={!canWrite}
          />
          <Input name="indicatedBy" label="Indicado por" value={form.indicatedBy} onChange={handleChange} disabled={!canWrite} />
          <Input
            name="brokersRelationship"
            label="Bancos/Corretoras com relacionamento"
            value={form.brokersRelationship}
            onChange={handleChange}
            disabled={!canWrite}
          />
          <Input
            name="acceptNewInstitutions"
            label="Aceita novas instituições?"
            value={form.acceptNewInstitutions}
            onChange={handleChange}
            disabled={!canWrite}
          />

          <div className="md:col-span-3">
            <Textarea
              name="expectedResult"
              label="Mini bio do cliente"
              value={form.expectedResult}
              onChange={handleChange}
              disabled={!canWrite}
            />
          </div>
        </div>

        <div className="mt-12">
          <h2 className="text-blueDarker text-xl font-semibold mb-4">Beneficiários</h2>
          <BeneficiariesFormList
            beneficiaries={form.beneficiariesAttributes || []}
            onChange={(updatedList) => setForm((prev) => ({ ...prev, beneficiariesAttributes: updatedList }))}
            canWrite={canWrite}
          />
        </div>

        <div className="mt-12">
          <h2 className="text-blueDarker text-xl font-semibold mb-4">Responsável financeiro</h2>
          <FinancialOfficerFormList
            officer={form.financialOfficer}
            onChange={(updated) => setForm((prev) => ({ ...prev, financialOfficer: updated }))}
            canWrite={canWrite}
          />
        </div>

        {canWrite && (
          <div className="flex justify-end mt-8">
            <p className="text-red-600 text-sm mr-auto">{errorMessage}</p>
            <Button type="submit" disabled={loading}>
              SALVAR E CONTINUAR
            </Button>
          </div>
        )}
      </form>
    </div>
  );
};

export default PersonalTab;
